import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-bootstrap-typeahead/css/Typeahead.bs5.css';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DRIVER_TYPE } from 'khufu-legos';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Nav, ProgressBar, Row } from 'react-bootstrap';
import { Token, Typeahead } from 'react-bootstrap-typeahead';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { useNavigate } from 'react-router-dom';

import { createOrder, getDrivers } from '../../../apis/queries';
import SubmitButton from '../../../components/Buttons/SubmitButton';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { Customer, OrderPrepare } from '../../../types';
import { PREPARED_ORDER_KEY } from '../../../utils/tenant';
import { GET_DRIVERS_KEY } from '../../Drivers/Drivers';
import { GET_EMPTIES_KEY } from '../../Empties/Empties';
import { EMPTIES_HISTORY_QUERY_KEY } from '../../Empties/EmptiesHistory';
import { STOCK_ADJUSTMENTS_QUERY_KEY } from '../../StockAdjustment/StockAdjustment';
import OrderSummary from '../OrderDetailPage/OrderSummary';
import { ORDERS_QUERY_KEY } from '../Orders';

interface StepThreeParams {
  setActiveStep: (step: number) => void;
  activeStep: number;
  customer: Customer;
}

const StepThree = ({ setActiveStep, activeStep, customer }: StepThreeParams) => {
  const isMounted = useRef(true);
  const preparedOrder = localStorage.getItem(PREPARED_ORDER_KEY);
  const cart: OrderPrepare = preparedOrder ? JSON.parse(preparedOrder) : {};

  const [requiresDriver, setRequiresDriver] = useState(false);
  const [requiresMotorboy, setRequiresMotorboy] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDrivers, setSelectedDrivers] = useState<string[]>([]);
  const [selectedMotorboys, setSelectedMotorboys] = useState<string[]>([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comments, setComments] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Retrieve orderPrepareId from localStorage
  const orderPrepareId = JSON.parse(localStorage.getItem(PREPARED_ORDER_KEY) || '{}')._id || '';

  const mutation = useMutation({
    mutationFn: ({
      orderPrepareId,
      driverIds,
      motorboyIds,
      comments
    }: {
      orderPrepareId: string;
      driverIds: string[];
      motorboyIds: string[];
      comments?: string;
    }) => {
      return createOrder(orderPrepareId, driverIds, motorboyIds, comments);
    },
    onSuccess: () => {
      setShowConfirmation(true);
    },
    onError: error => {
      // Handle error
      console.error('Error creating order:', error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [ORDERS_QUERY_KEY, STOCK_ADJUSTMENTS_QUERY_KEY, EMPTIES_HISTORY_QUERY_KEY, GET_EMPTIES_KEY]
      });
    }
  });

  const handleCreateOrder = () => {
    const orderData: {
      orderPrepareId: string;
      driverIds: string[];
      motorboyIds: string[];
      comments?: string;
    } = {
      orderPrepareId,
      driverIds: requiresDriver && selectedDrivers?.length ? selectedDrivers : [],
      motorboyIds: requiresMotorboy && selectedMotorboys?.length ? selectedMotorboys : []
    };

    const trimmedComments = comments.trim();
    if (trimmedComments) {
      orderData.comments = trimmedComments;
    }

    mutation.mutate(orderData);
  };

  const mutationLoading = mutation?.isPending;

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleCloseConfirmation = () => {
    setLoading(true);

    setTimeout(() => {
      if (isMounted.current) {
        setLoading(false);
        setShowConfirmation(false);
        navigate('/orders/all');
      }
    }, 2000);
  };

  return (
    <div className="main-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12} lg={12} xl={12} className="py-6">
            <Row className="justify-content-center">
              <Col xs={12} md={10} lg={8} xl={6} className="text-center">
                <h6 className="mb-4 text-uppercase text-muted">Step {activeStep} of 3</h6>
                <Col>
                  <ProgressBar variant="primary" now={100} className="progress-sm" />
                </Col>
                <h1 className="my-4">Let’s get some last details.</h1>
                <p className="mb-5 text-muted">Confirm your order before you continue.</p>
              </Col>
            </Row>

            <Row className="justify-content-center mt-2 text-center">
              <Col className="d-flex justify-content-center align-items-center">
                <div className="d-flex align-items-center mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="requiresDriverToggle"
                      checked={requiresDriver}
                      onChange={() => setRequiresDriver(prev => !prev)}
                    />
                    <label className="form-check-label" htmlFor="requiresDriverToggle">
                      {requiresDriver}
                    </label>
                  </div>
                  <h5 className="me-3 mb-0">Does this order require a driver?</h5>
                </div>
              </Col>
            </Row>

            {requiresDriver && (
              <Row className="justify-content-center mt-2 text-center">
                <h3 className="my-4">Assign Drivers</h3>
                <DriverTypeAhead setSelectedDrivers={setSelectedDrivers} />
              </Row>
            )}

            {selectedDrivers.length > 0 && (
              <Row className="justify-content-center mt-2 text-center">
                <Col className="d-flex justify-content-center align-items-center">
                  <div className="d-flex align-items-center mb-3 mt-4">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="requiresMotorboyToggle"
                        checked={requiresMotorboy}
                        onChange={() => setRequiresMotorboy(prev => !prev)}
                      />
                      <label className="form-check-label" htmlFor="requiresMotorboyToggle">
                        {requiresMotorboy}
                      </label>
                    </div>
                    <h5 className="me-3 mb-0">Does this order require a motorboy?</h5>
                  </div>
                </Col>
              </Row>
            )}

            {requiresMotorboy && (
              <Row className="justify-content-center mt-2 text-center">
                <h3 className="my-4">Assign Motorboys</h3>
                <MotorboyTypeAhead setSelectedMotorboys={setSelectedMotorboys} />
              </Row>
            )}

            <>
              <Row className="justify-content-center mt-6">
                <Col xs={12} lg={10} xl={8}>
                  <OrderSummary customer={customer} orderPrepare={cart} showNotes={false} title="Order Summary" />
                </Col>
              </Row>

              <hr className="my-5" />
              <Nav className="row align-items-center">
                <Col xs="auto">
                  <Button variant="white" size="lg" onClick={() => setActiveStep(activeStep - 1)}>
                    Back
                  </Button>
                </Col>
                <Col className="text-center">
                  <h6 className="text-uppercase text-muted mb-0">Step {activeStep} of 3</h6>
                </Col>
                <Col xs="auto">
                  <SubmitButton
                    buttonText="Next"
                    onClick={() => setShowCommentModal(true)}
                    disabled={mutationLoading}
                    loading={mutationLoading}
                  />
                </Col>
              </Nav>
            </>
          </Col>
        </Row>
      </Container>

      <ConfirmationModal
        show={showConfirmation}
        onHide={handleCloseConfirmation}
        title="Order Confirmation"
        bodyContent={
          <div>
            <img src="/img/kanban/confirmation.jpg" alt="confirmation" style={{ width: '150px' }} />
            <p>Your order has been created successfully!</p>
          </div>
        }
        footerButtonText="Close"
        loading={loading}
      />

      <ConfirmationModal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        title="Add Comments (Optional)"
        bodyContent={
          <div className="w-100">
            <textarea
              className="form-control w-100"
              rows={3}
              placeholder="Enter any notes or delivery instructions..."
              value={comments}
              onChange={e => setComments(e.target.value)}
            />
          </div>
        }
        footerButtonText="Create Order"
        onFooterButtonClick={() => {
          handleCreateOrder();
          setShowCommentModal(false);
        }}
        loading={mutationLoading}
      />
    </div>
  );
};

export default StepThree;

interface Driver {
  _id: string;
  name: string;
}
interface Motorboy {
  _id: string;
  name: string;
}

const DriverTypeAhead = ({ setSelectedDrivers }: { setSelectedDrivers: (drivers: string[]) => void }) => {
  const { data: drivers, isLoading: driversLoading } = useQuery({
    queryKey: [GET_DRIVERS_KEY],
    queryFn: () => getDrivers({ isActive: true }),
    select: data => data?.results?.filter(driver => driver.driverType === DRIVER_TYPE.DRIVER) || []
  });

  const handleDriverSelection = (selected: Option[]) => {
    const selectedDrivers = selected as Driver[];
    const driverIds = selectedDrivers.map(driver => driver._id);
    setSelectedDrivers(driverIds);
  };

  return (
    <Row className="justify-content-center">
      <Col xs={12} lg={10} xl={8}>
        <Typeahead
          id="driver-typeahead"
          labelKey="name"
          // multiple
          onChange={handleDriverSelection}
          options={drivers as Option[]}
          placeholder="Select driver..."
          renderToken={(option, { onRemove }, index) => (
            <Token key={index} onRemove={onRemove} option={option}>
              {(option as Driver).name}
            </Token>
          )}
          disabled={driversLoading}
          isLoading={driversLoading}
        />
      </Col>
    </Row>
  );
};

const MotorboyTypeAhead = ({ setSelectedMotorboys }: { setSelectedMotorboys: (motorboys: string[]) => void }) => {
  const { data: motorboys, isLoading: motorboysLoading } = useQuery({
    queryKey: [GET_DRIVERS_KEY],
    queryFn: () => getDrivers({ isActive: true }),
    select: data => data?.results?.filter(driver => driver.driverType === DRIVER_TYPE.MOTORBOY) || []
  });

  const handleMotorboySelection = (selected: Option[]) => {
    const selectedMotorboys = selected as Motorboy[];
    const motorboyIds = selectedMotorboys.map(motorboy => motorboy._id);
    setSelectedMotorboys(motorboyIds);
  };

  return (
    <Row className="justify-content-center">
      <Col xs={12} lg={10} xl={8}>
        <Typeahead
          id="motorboy-typeahead"
          labelKey="name"
          // multiple
          onChange={handleMotorboySelection}
          options={motorboys as Option[]}
          placeholder="Select motorboy..."
          renderToken={(option, { onRemove }, index) => (
            <Token key={index} onRemove={onRemove} option={option}>
              {(option as Motorboy).name}
            </Token>
          )}
          disabled={motorboysLoading}
          isLoading={motorboysLoading}
        />
      </Col>
    </Row>
  );
};
